// src/patients/components/chart/chart-timeline.tsx
//
// The patient chart's Timeline (Andrew, 4 Nov; Samer's demo `?view=timeline`), the chart's lead tab in place of Overview behind
// `?x=chart-timeline` (dev default ON; `?x=-chart-timeline` shows the Overview). Lanes over time: Visits, Labs, Imaging &
// studies, Medications, Plan & interventions; a 1Y / 3Y / 5Y / All window; a Legend; an Events filter (which lanes show).
// Every mark and bar comes from an existing read (lib/chart-timeline.ts says which). Nothing is invented: a lane with nothing
// in the window says so, and the gaps are named under the chart. Andrew's rulings (4 Nov): (a) the lab trend cards cover every
// test flagged H / L / critical in the window (a separate component, mounted below); (b) the demo's Workup and Patient view
// sub-tabs are out of scope. The look is the demo's (4 Nov, "match exactly the demo"): the header row with the count, Legend,
// the 1Y / 3Y / 5Y / All switch and Events; one card of lanes (timeline-lanes.tsx). Colours are contract tokens.
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, SlidersHorizontal } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTitle,
  AnchoredPopoverTrigger,
  Button,
  Checkbox,
  Segmented,
} from '@scripsteam/scrips-signal-ds';
import { post } from '@/shared/api/api-client';
import type { AppointmentForPatientResponse } from '@/types/scheduling-api';
import { usePatientExternalLabReports, usePatientLabRequests } from '@/patients/services/use-patient-lab-results';
import { usePatientLabs } from '@/patients/services/use-patient-labs';
import { usePatientMedications } from '@/patients/services/use-patient-medications';
import { useCarePlans } from '@/encounter/services/use-care-plans';
import { LabTrendSection } from './lab-trend-section';
import { MarkIcon, TimelineLanes } from './timeline-lanes';
import { clinicDayOf } from '@/patients/lib/lab-trend-cards';
import {
  TIMELINE_LANES,
  TIMELINE_RANGES,
  imagingMarks,
  labMarks,
  laneEnd,
  medicationBars,
  planBars,
  rangeWindow,
  visitMarks,
  type TimelineBar,
  type TimelineLane,
  type TimelineMark,
  type TimelineRange,
} from '@/patients/lib/chart-timeline';

/** The visits read, as the chart's appointments read but with room for years of history (that one shows the first 10). */
const VISIT_PAGE_SIZE = 200;
function useTimelineVisits(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-timeline-visits', patientId],
    queryFn: ({ signal }) =>
      post<AppointmentForPatientResponse>(
        '/api/Appointment/ForPatient',
        { patientId: patientId ?? '', pageNumber: 1, pageSize: VISIT_PAGE_SIZE, includePrevious: true, LoggedInUserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        { signal },
      ),
    enabled: !!patientId,
    staleTime: 30_000,
  });
}

/** A window end as the CLINIC's calendar day (Dubai), YYYY-MM-DD: at 01:00 in Dubai it is already tomorrow there, not in UTC. */
const clinicDay = (t: number) => clinicDayOf(t) ?? new Date(t).toISOString().slice(0, 10);

/** The legend, as the demo's (only the marks these reads can draw). */
const LEGEND: readonly { label: string; face: ReactNode }[] = [
  { label: 'Visit', face: <MarkIcon shape="dot" size={12} /> },
  { label: 'Lab draw', face: <MarkIcon shape="ring" /> },
  { label: 'Result outside lab range (above, below)', face: <span className="inline-flex gap-1"><MarkIcon shape="up" /><MarkIcon shape="down" /></span> },
  { label: 'Critical result', face: <MarkIcon shape="alert" /> },
  { label: 'Ordered, no result yet (lab, imaging)', face: <span className="inline-flex gap-1"><MarkIcon shape="hollow-dot" /><MarkIcon shape="hollow-square" /></span> },
  { label: 'On the medication list', face: <svg width="32" height="8" viewBox="0 0 32 8" aria-hidden className="block"><rect x="0.5" y="1" width="31" height="6" rx="3" fill="var(--cat-sky-100)" stroke="var(--cat-sky-500)" /></svg> },
  { label: 'Faded: after today', face: null },
];

/** Below this width (px) the header goes compact: the count on its own line, Legend and Events as icon buttons. */
const COMPACT_BELOW = 560;

export interface ChartTimelineProps {
  patientId: string;
  /** Today, for the window (tests pin it). */
  now?: number;
}

export function ChartTimeline({ patientId, now = Date.now() }: ChartTimelineProps) {
  const [range, setRange] = useState<TimelineRange>('5Y');
  const [shown, setShown] = useState<ReadonlySet<TimelineLane>>(() => new Set(TIMELINE_LANES.map((l) => l.id)));
  const [legendOpen, setLegendOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const visits = useTimelineVisits(patientId);
  const reports = usePatientExternalLabReports(patientId);
  const requests = usePatientLabRequests(patientId);
  const orders = usePatientLabs(patientId);
  const meds = usePatientMedications(patientId, false);
  const plans = useCarePlans(patientId);

  const marks = useMemo<TimelineMark[]>(
    () => [
      ...visitMarks(visits.data?.appointments),
      ...labMarks(reports.data?.reports, requests.data?.patientLabOrderDetails),
      ...imagingMarks(orders.data),
    ],
    [visits.data, reports.data, requests.data, orders.data],
  );
  const bars = useMemo<TimelineBar[]>(() => [...medicationBars(meds.data), ...planBars(plans.data)], [meds.data, plans.data]);
  const earliest = useMemo(() => {
    const ts = [...marks.map((m) => m.t), ...bars.map((b) => b.start)];
    return ts.length ? Math.min(...ts) : null;
  }, [marks, bars]);
  // One start for the lanes and the trend cards: today back 1 / 3 / 5 years ("All": from the earliest item). The cards end today;
  // the lanes run on to 31 December of the clinic year, or to a later BOOKED visit (visits only), capped 90 days past that
  // 31 December (laneEnd), as the demo's do, so a booked visit shows, faded.
  const { from, to } = rangeWindow(range, now, earliest);
  const axis = { from, to: laneEnd(now, marks) };
  const inWindow = (t: number) => t >= axis.from && t <= axis.to;

  const loading = [visits, reports, requests, orders, meds, plans].some((q) => q.isLoading);
  // Each lane draws as soon as its own read answers (dev, 4 Nov: the 200-visit read takes ~8 s, the others under 1 s).
  const laneLoading: Record<TimelineLane, boolean> = {
    visits: visits.isLoading,
    labs: reports.isLoading || requests.isLoading,
    imaging: orders.isLoading,
    medications: meds.isLoading,
    plan: plans.isLoading,
  };
  // A narrow host (the encounter's chart column, ~370 px): the count gets its own line and Legend / Events are icon buttons.
  const [compact, setCompact] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const read = () => setCompact(el.clientWidth > 0 && el.clientWidth < COMPACT_BELOW);
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const failed: Record<TimelineLane, boolean> = {
    visits: visits.isError,
    // Either labs read failing is said: a failed reports read would otherwise leave only the pending orders, the results lost.
    labs: reports.isError || requests.isError,
    imaging: orders.isError,
    medications: meds.isError,
    plan: plans.isError,
  };

  const lanes = TIMELINE_LANES.filter((l) => shown.has(l.id));
  const barsIn = (lane: TimelineLane) => bars.filter((b) => b.lane === lane && b.start <= axis.to && (b.end ?? now) >= axis.from);
  const visibleCount = lanes.reduce(
    (n, l) => n + (l.id === 'medications' || l.id === 'plan' ? barsIn(l.id).length : marks.filter((m) => m.lane === l.id && inWindow(m.t)).length),
    0,
  );
  const anyComputed = barsIn('medications').some((b) => b.endComputed);
  // The trend cards' window, in the CLINIC's days (Dubai): the end is today there, the start the same day 1 / 3 / 5 years back.
  const toDay = clinicDay(to);
  const yearsBack = range === '1Y' ? 1 : range === '3Y' ? 3 : 5;
  const trendRange = { from: range === 'All' ? null : `${Number(toDay.slice(0, 4)) - yearsBack}${toDay.slice(4)}`, to: toDay };
  // The visits read is one page: when the patient has more, the page says so ("All" would otherwise look complete).
  const visitsCapped = (visits.data?.totalPage ?? 1) > 1 || (visits.data?.appointments?.length ?? 0) >= VISIT_PAGE_SIZE;
  const eventsLabel = `Events${shown.size < TIMELINE_LANES.length ? ` · ${shown.size}/${TIMELINE_LANES.length}` : ''}`;
  const toggleLane = (id: TimelineLane) =>
    setShown((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section ref={sectionRef} aria-label="Timeline" data-slot="chart-timeline" data-compact={compact ? 'true' : undefined} className="flex flex-col gap-2">
      <div data-slot="ct-head" className="flex flex-wrap items-center gap-2">
        <p data-slot="ct-count" className={compact ? 'm-0 w-full text-sm text-text-secondary' : 'm-0 min-w-0 flex-1 truncate text-sm text-text-secondary'}>
          {loading ? `${visibleCount} event${visibleCount === 1 ? '' : 's'} so far · loading…` : `${visibleCount} event${visibleCount === 1 ? '' : 's'} · events in the same week share one mark`}
        </p>
        <AnchoredPopover open={legendOpen} onOpenChange={setLegendOpen}>
          <AnchoredPopoverTrigger
            render={
              compact ? (
                <Button variant="secondary" size="sm" icon={<Info />} aria-label="Legend" />
              ) : (
                <Button variant="secondary" size="sm" icon={<Info />}>
                  Legend
                </Button>
              )
            }
          />
          <AnchoredPopoverContent aria-label="Timeline legend">
            <AnchoredPopoverTitle>Legend</AnchoredPopoverTitle>
            <ul aria-label="Legend" data-slot="ct-legend" className="m-0 flex list-none flex-col gap-2 p-0 text-sm text-text-secondary">
              {LEGEND.map((l) => (
                <li key={l.label} className="flex items-center gap-2">
                  {l.face}
                  {l.label}
                </li>
              ))}
            </ul>
          </AnchoredPopoverContent>
        </AnchoredPopover>
        <Segmented<TimelineRange>
          aria-label="Time range"
          variant="pill"
          value={range}
          onChange={setRange}
          options={TIMELINE_RANGES.map((r) => ({ label: r, value: r }))}
        />
        <AnchoredPopover open={filterOpen} onOpenChange={setFilterOpen}>
          <AnchoredPopoverTrigger
            render={
              compact ? (
                <Button variant="secondary" size="sm" icon={<SlidersHorizontal />} aria-label={eventsLabel} />
              ) : (
                <Button variant="secondary" size="sm" icon={<SlidersHorizontal />}>
                  {eventsLabel}
                </Button>
              )
            }
          />
          <AnchoredPopoverContent aria-label="Show lanes">
            <AnchoredPopoverTitle>Show lanes</AnchoredPopoverTitle>
            <div role="group" aria-label="Lanes shown" data-slot="ct-filter" className="flex flex-col gap-2">
              {TIMELINE_LANES.map((l) => (
                <Checkbox key={l.id} label={l.title} checked={shown.has(l.id)} onCheckedChange={() => toggleLane(l.id)} />
              ))}
            </div>
          </AnchoredPopoverContent>
        </AnchoredPopover>
      </div>

      <TimelineLanes lanes={lanes} marks={marks} bars={bars} range={range} from={axis.from} to={axis.to} now={now} failed={failed} loading={laneLoading} />

      {/* The lab trend cards and Compare results, below the lanes, for this window (Andrew's ruling (a): every test flagged H / L /
          critical in it). ISO dates, both inclusive; "All" has no start. */}
      <LabTrendSection patientId={patientId} range={trendRange} />
      <ul aria-label="What the timeline cannot show yet" data-slot="ct-gaps" className="m-0 flex list-none flex-col gap-1 p-0 text-xs text-text-secondary">
        {shown.has('imaging') ? <li>Imaging & studies: orders only. Imaging results have no read yet.</li> : null}
        {visitsCapped ? <li>Visits: the latest {VISIT_PAGE_SIZE} appointments are shown; older ones are not.</li> : null}
        {anyComputed ? <li>Medications: where no stop date is recorded, the end is computed from the recorded duration.</li> : null}
      </ul>
    </section>
  );
}
